import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Avatar } from '../../services/userService';
import {
  AVATAR_COLORS,
  AVATAR_EMOJIS,
  getAvatarsByRole,
  getRandomAvatar,
} from '../../utils/avatarConfig';
import ShuffleIcon from '@mui/icons-material/Shuffle';

interface AvatarSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (avatar: Avatar) => void;
  currentAvatar?: Avatar;
  role: 'student' | 'teacher';
}

/**
 * AvatarSelector component
 * Allows users to select an emoji and color for their avatar
 */
const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  open,
  onClose,
  onSelect,
  currentAvatar,
  role,
}) => {
  const [selectedEmoji, setSelectedEmoji] = useState(currentAvatar?.emoji || '🎓');
  const [selectedColor, setSelectedColor] = useState(currentAvatar?.color || '#3B82F6');

  const availableEmojis = getAvatarsByRole(role);

  const handleRandomize = () => {
    const randomAvatar = getRandomAvatar(role);
    setSelectedEmoji(randomAvatar.emoji);
    setSelectedColor(randomAvatar.color);
  };

  const handleSave = () => {
    onSelect({
      emoji: selectedEmoji,
      color: selectedColor,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Choose Your Avatar</Typography>
          <Tooltip title="Random Avatar">
            <IconButton onClick={handleRandomize} color="primary">
              <ShuffleIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Preview */}
        <Box display="flex" justifyContent="center" mb={3} mt={2}>
          <Paper
            elevation={3}
            sx={{
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: selectedColor,
              borderRadius: '50%',
            }}
          >
            <Typography variant="h1" sx={{ fontSize: '4rem' }}>
              {selectedEmoji}
            </Typography>
          </Paper>
        </Box>

        {/* Emoji Selection */}
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Choose an Emoji
        </Typography>
        <Box
          sx={{
            maxHeight: 200,
            overflowY: 'auto',
            mb: 3,
            p: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
          }}
        >
          <Grid container spacing={1}>
            {availableEmojis.map((option) => (
              <Grid item key={option.emoji}>
                <Tooltip title={option.label}>
                  <Paper
                    elevation={selectedEmoji === option.emoji ? 3 : 0}
                    sx={{
                      width: 50,
                      height: 50,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      border:
                        selectedEmoji === option.emoji ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => setSelectedEmoji(option.emoji)}
                  >
                    <Typography variant="h5">{option.emoji}</Typography>
                  </Paper>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Color Selection */}
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Choose a Color
        </Typography>
        <Grid container spacing={1}>
          {AVATAR_COLORS.map((color) => (
            <Grid item key={color.value}>
              <Tooltip title={color.label}>
                <Paper
                  elevation={selectedColor === color.value ? 3 : 0}
                  sx={{
                    width: 50,
                    height: 50,
                    backgroundColor: color.value,
                    cursor: 'pointer',
                    border: selectedColor === color.value ? '3px solid #000' : '1px solid #e0e0e0',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => setSelectedColor(color.value)}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Avatar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarSelector;
